import { Sentry } from "../sentryInstrumentReact";
import React, { createContext, useEffect, useContext, useState, useMemo, useCallback} from 'react';

const CartContext = createContext();

export function useCart() {
  return useContext(CartContext);
}

function clearErrorAtPath(errors, path) {
  if (!errors || path.length === 0) {
    return errors;
  }

  const [head, ...tail] = path;
  if (tail.length === 0) {
    if (!(head in errors)) {
      return errors;
    }
    const { [head]: removed, ...rest } = errors;
    return rest;
  }

  if (!errors[head] || typeof errors[head] !== "object") {
    return errors;
  }

  const child = clearErrorAtPath(errors[head], tail);
  if (Object.keys(child).length === 0) {
    const { [head]: removed, ...rest } = errors;
    return rest;
  }

  return { ...errors, [head]: child };
}

export function CartProvider({children}) {
  const [cart, setCart] = useState({
    items: [],
  });
  const [cartSubtotal, setCartSubtotal] = useState(0);
  const [cartSubtotalWithDiscount, setCartSubtotalWithDiscount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);

  const [rendering, setRendering] = useState(true);
  const [cartTax, setCartTax] = useState(0);
  const [shipping, setShipping] = useState({
    cost: 0,
    rateId: "",
  });
  const [donation, setDonation] = useState(0);
  const [discountCode, setDiscountCode] = useState('');
  const [discountLocked, setDiscountLocked] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [shippingSameAsBilling, setShippingSameAsBilling] = useState(true);
  const [shippingRequired, setShippingRequired] = useState(true);
  const [fetchingShippingCost, setFetchingShippingCost] = useState(false); // only used to animate loading
  const [fetchingTaxCost, setFetchingTaxCost] = useState(false); // only used to animate loading
  const [checkoutErrors, setCheckoutErrors] = useState({
    // exp_month: ["this is a thing"],
    // exp_year: ["year is incorrect"],
    // region: ["please select a billing region"],
    // shipping_region: ["please select a shipping region"],
    // shipping_country: ["please select a shipping country"],
    // country: ["please select a billing country"],
    // shipping_method: ["please choose a proper shipping method"],
  });
  const [checkoutFormData, setCheckoutFormData] = useState({
    billing: {
      firstName: '',
      lastName: '',
      organization: '',
      email: '',
      phone: '',
      street1: '',
      street2: '',
      city: '',
      region: '',
      postalCode: '',
      country: '',
      addressComplete: false,
    },
    shipping: {
      firstName: '',
      lastName: '',
      organization: '',
      street1: '',
      street2: '',
      city: '',
      region: '',
      postalCode: '',
      country: '',
      addressComplete: false,
    },
  });


  const clearCheckoutFieldError = useCallback((path) => {
    setCheckoutErrors((prev) => clearErrorAtPath(prev, path));
  }, []);

  // get products in cart
  useEffect(() => {
    async function fetchCartData() {
      try {
        const response = await fetch("/store/_api/cart/");
        const cartObject = await response.json();
        setCart(cartObject);
        setDonation(cartObject.donation ? parseFloat(cartObject.donation).toFixed(2) : '');

        setCartSubtotal(parseFloat(cartObject.subtotal).toFixed(2));
        setCartSubtotalWithDiscount(parseFloat(cartObject.discounted_subtotal).toFixed(2));
        setShippingRequired(cartObject.shipping_required);
        const cartTotalWithDonation = parseFloat(cartObject.discounted_subtotal) + (parseFloat(cartObject.donation || 0));
        setCartTotal(cartTotalWithDonation.toFixed(2));

        setRendering(false);
        setDiscountAmount(parseFloat(cartObject.discount_amount).toFixed(2));
        setDiscountCode(cartObject.discount_code);
        if(cartObject.discount_code !== '') {
          setDiscountLocked(true);
        }
      } catch(error) {
        Sentry.captureException?.(error, {stage: "get cart context data"});
      }
    }
    fetchCartData();

  }, []);

  const contextValue = useMemo(() => {
    console.log('[CartProvider] creating context value');
    return {
      cart,
      setCart,
      cartTotal,
      setCartTotal,
      cartSubtotal,
      setCartSubtotal,
      cartSubtotalWithDiscount,
      setCartSubtotalWithDiscount,
      rendering,
      setRendering,
      cartTax,
      setCartTax,
      shipping,
      setShipping,
      donation,
      setDonation,
      discountCode,
      setDiscountCode,
      discountLocked,
      setDiscountLocked,
      discountAmount,
      setDiscountAmount,
      checkoutSubmitting,
      setCheckoutSubmitting,
      shippingSameAsBilling,
      setShippingSameAsBilling,
      shippingRequired,
      setShippingRequired,
      checkoutErrors,
      setCheckoutErrors,
      clearCheckoutFieldError,
      checkoutFormData,
      setCheckoutFormData,
      fetchingShippingCost,
      setFetchingShippingCost,
      fetchingTaxCost,
      setFetchingTaxCost
    };
  }, [
    cart,
    cartTotal,
    cartSubtotal,
    cartSubtotalWithDiscount,
    rendering,
    cartTax,
    shipping,
    donation,
    discountCode,
    discountLocked,
    discountAmount,
    checkoutSubmitting,
    shippingSameAsBilling,
    shippingRequired,
    checkoutErrors,
    clearCheckoutFieldError,
    checkoutFormData,
    fetchingShippingCost,
    fetchingTaxCost
  ]);

  console.log('[CartProvider] render');

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  )
}
